import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api, { mediaUrl } from "../lib/api";
import ProductCard from "../components/ProductCard";

function Section({ children, className = "" }) {
  return <section className={`luxy-container py-16 sm:py-20 lg:py-28 ${className}`}>{children}</section>;
}

export default function Home() {
  const [hc, setHc] = useState({});
  const [arrivals, setArrivals] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, a, f, b] = await Promise.all([
          api.get("/content/homepage"),
          api.get("/products", { params: { new_arrival: true, limit: 8, sort: "newest" } }),
          api.get("/products", { params: { featured: true, limit: 4 } }),
          api.get("/brands"),
        ]);
        setHc(c.data.data || {});
        setArrivals(a.data.products);
        setFeatured(f.data.products);
        setBrands(b.data);
      } catch {}
    })();
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[82vh] min-h-[560px] w-full overflow-hidden bg-espresso">
        {hc.hero_image && <img src={mediaUrl(hc.hero_image)} alt="LUXYREIGN editorial"
          className="absolute inset-0 w-full h-full object-cover object-[center_25%]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-pitch/70 via-pitch/20 to-pitch/30" />
        <div className="relative h-full luxy-container flex flex-col justify-end pb-16 sm:pb-20 lg:pb-28">
          <div className="max-w-2xl animate-fade-in-up">
            <p className="eyebrow text-ivory/70 mb-5">The Curated Archive</p>
            <h1 className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {hc.hero_heading || "TIMELESS LUXURY. REIMAGINED."}
            </h1>
            <p className="mt-6 text-ivory/80 text-base sm:text-lg font-light max-w-lg leading-relaxed">
              {hc.hero_subtext || "Curated pre-owned luxury pieces, selected for those who appreciate enduring style."}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/shop" data-testid="hero-cta-shop"
                className="inline-flex items-center justify-center bg-ivory text-pitch text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-champagne-soft transition-colors">
                {hc.hero_primary_cta || "Shop The Collection"}
              </Link>
              <Link to="/shop?new_arrival=true" data-testid="hero-cta-explore"
                className="inline-flex items-center justify-center gap-2 border border-ivory/60 text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-ivory hover:text-pitch transition-colors">
                {hc.hero_secondary_cta || "Explore New Arrivals"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <Section>
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">Freshly Sourced</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-pitch tracking-tight">New Arrivals</h2>
          </div>
          {arrivals.length > 0 && (
            <Link to="/shop?new_arrival=true" className="hidden sm:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1 hover:text-champagne-dark hover:border-champagne-dark transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {arrivals.length === 0 ? (
          <div className="border border-linen bg-bone/50 py-20 sm:py-28 text-center px-6" data-testid="home-empty-collection">
            <p className="eyebrow mb-5">The Atelier</p>
            <h3 className="font-serif font-light text-3xl sm:text-4xl text-pitch tracking-tight">The next collection is coming soon</h3>
            <p className="text-charcoal/70 mt-5 max-w-xl mx-auto leading-relaxed">
              LUXYREIGN is currently curating its latest selection of pre-owned luxury pieces. Register your interest and be the first to know when new arrivals are released.
            </p>
            <Link to="/contact" className="inline-block mt-8 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-charcoal transition-colors">Register Your Interest</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {arrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </Section>

      {/* SHOP BY BRAND */}
      <div className="bg-bone">
        <Section className="!py-16 sm:!py-20">
          <p className="eyebrow mb-3 text-center">The Maisons</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-pitch tracking-tight text-center mb-12">Shop by Brand</h2>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-6">
            {brands.slice(0, 10).map((b) => (
              <Link key={b.id} to={`/shop?brand=${encodeURIComponent(b.name)}`}
                data-testid={`brand-link-${b.slug}`}
                className="font-serif text-2xl sm:text-3xl text-charcoal/70 hover:text-pitch transition-colors">
                {b.name}
              </Link>
            ))}
          </div>
        </Section>
      </div>

      {/* FEATURED COLLECTION */}
      {featured.length > 0 && (
        <Section>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="eyebrow mb-3">Curator's Selection</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-pitch tracking-tight">Featured Collection</h2>
            </div>
            <Link to="/shop?featured=true" className="hidden sm:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1 hover:text-champagne-dark transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      {/* THE LUXYREIGN STANDARD */}
      <div className="bg-espresso text-ivory">
        <Section>
          <p className="eyebrow text-champagne mb-3 text-center">Why LUXYREIGN</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-ivory tracking-tight text-center mb-16">The LUXYREIGN Standard</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {(hc.standard_points || []).map((s, i) => (
              <div key={i} className="text-center sm:text-left">
                <div className="font-mono text-champagne text-sm mb-4">0{i + 1}</div>
                <h3 className="font-serif text-2xl text-ivory mb-3">{s.title}</h3>
                <p className="text-sm text-linen/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* AUTHENTICATION EDITORIAL */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="aspect-[4/5] bg-bone overflow-hidden order-1 lg:order-none">
            {hc.authentication_image && <img src={mediaUrl(hc.authentication_image)} alt="Authentication process"
              className="w-full h-full object-cover" />}
          </div>
          <div>
            <p className="eyebrow mb-4">Trust & Provenance</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-pitch tracking-tight leading-tight mb-6">
              {hc.authentication_heading || "The LUXYREIGN Standard"}
            </h2>
            <p className="text-charcoal/80 leading-relaxed mb-8">{hc.authentication_text}</p>
            <Link to="/authentication" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1 hover:text-champagne-dark transition-colors">
              Our Authentication Approach <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* ABOUT */}
      <div className="bg-bone">
        <Section className="text-center !py-20">
          <p className="eyebrow mb-4">The House</p>
          {hc.logo && <img src={mediaUrl(hc.logo)} alt="LUXYREIGN" className="w-24 h-24 mx-auto mb-6 object-contain" />}
          <h2 className="font-serif text-3xl sm:text-4xl text-pitch tracking-tight mb-6">{hc.about_heading || "About LUXYREIGN"}</h2>
          <p className="max-w-2xl mx-auto text-charcoal/80 leading-relaxed">{hc.about_text}</p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1 hover:text-champagne-dark transition-colors">
            Read More <ArrowRight className="w-4 h-4" />
          </Link>
        </Section>
      </div>
    </div>
  );
}
